<template>
  <div class="cm-contenedor">

    <!-- --- ENCABEZADO --- -->
    <div class="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
      <div>
        <h4 class="cm-titulo mb-1">
          <i class="bi bi-file-earmark-arrow-up me-2"></i>Carga Masiva de Productos
        </h4>
        <p class="cm-subtitulo mb-0">Importa productos desde un archivo Excel (.xlsx / .xls)</p>
      </div>
      <a :href="urlPlantilla" download="plantilla_productos.xlsx" class="btn btn-outline-primary btn-sm">
        <i class="bi bi-download me-1"></i>Descargar plantilla
      </a>
    </div>

    <!-- --- PASO 1: Seleccionar archivo y mapear columnas --- -->
    <div class="cm-seccion mb-4">
      <div class="cm-seccion__cabecera">
        <i class="bi bi-1-circle-fill me-2"></i>Seleccionar archivo y mapear columnas
      </div>
      <div class="cm-seccion__cuerpo">

        <!-- Fila: archivo + acción duplicado -->
        <div class="row g-3 mb-3">
          <div class="col-md-6">
            <label class="form-label fw-semibold">Archivo Excel <span class="text-danger">*</span></label>
            <input
              ref="inputArchivo"
              type="file"
              class="form-control"
              accept=".xlsx,.xls"
              @change="onArchivoSeleccionado"
            />
            <div class="form-text cm-texto-secundario">Formatos aceptados: .xlsx y .xls</div>
          </div>
          <div class="col-md-6">
            <label class="form-label fw-semibold">Acción si el producto ya existe</label>
            <select class="form-select" v-model="accionDuplicado">
              <option value="actualizar_todo">Actualizar todo (datos + precios + stock)</option>
              <option value="actualizar_precios">Actualizar solo precios (costo + listas)</option>
              <option value="actualizar_stock">Actualizar solo stock / existencia</option>
              <option value="solo_crear">Crear solo nuevos (ignorar duplicados)</option>
              <option value="ignorar">Ignorar completamente (no modificar nada)</option>
            </select>
          </div>
        </div>

        <!-- Configuración avanzada: IVA por defecto -->
        <div class="mb-3">
          <button class="btn btn-link btn-sm p-0 cm-link-avanzado" type="button" @click="mostrarAvanzado = !mostrarAvanzado">
            <i :class="mostrarAvanzado ? 'bi-chevron-down' : 'bi-chevron-right'" class="bi me-1"></i>
            Configuración avanzada
          </button>
          <div v-if="mostrarAvanzado" class="mt-2 cm-panel-avanzado">
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label small fw-semibold">IVA por defecto</label>
                <select class="form-select form-select-sm" v-model.number="ivaPorDefecto">
                  <option :value="15">15 % — IVA 15 %</option>
                  <option :value="0">0 % — IVA 0 %</option>
                  <option :value="5">5 % — IVA 5 %</option>
                </select>
                <div class="form-text small">Se aplica a productos sin columna IVA mapeada</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mapeo de columnas (solo aparece tras cargar archivo) -->
        <div v-if="columnasArchivo.length > 0">
          <div class="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <span class="fw-semibold cm-texto-primario">
              <i class="bi bi-arrow-left-right me-1"></i>Mapeo de columnas
            </span>
            <span class="cm-badge-primary">{{ columnasArchivo.length }} columnas detectadas</span>
          </div>
          <div class="row g-2 mb-3">
            <div v-for="campo in camposMapeo" :key="campo.key" class="col-xl-2 col-lg-3 col-md-4 col-sm-6">
              <label class="form-label small fw-semibold mb-1">
                {{ campo.etiqueta }}
                <span v-if="campo.requerido" class="text-danger">*</span>
                <span v-else class="cm-texto-muted" style="font-weight:400"> (opc.)</span>
              </label>
              <select class="form-select form-select-sm" v-model="mapeoColumnas[campo.key]">
                <option value="">— No mapear —</option>
                <option v-for="col in columnasArchivo" :key="col" :value="col">{{ col }}</option>
              </select>
            </div>
          </div>

          <div class="d-flex gap-2 align-items-center flex-wrap">
            <button
              class="btn btn-primary"
              @click="aplicarMapeo"
              :disabled="!mapeoValido || procesandoArchivo"
            >
              <span v-if="procesandoArchivo" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="bi bi-check2-circle me-2"></i>
              Aplicar mapeo y previsualizar
            </button>
            <button v-if="filas.length > 0" class="btn btn-outline-secondary btn-sm" @click="limpiarTodo">
              <i class="bi bi-x-circle me-1"></i>Limpiar
            </button>
            <span v-if="!mapeoValido" class="small cm-texto-error">
              <i class="bi bi-exclamation-triangle me-1"></i>Complete los campos obligatorios (*)
            </span>
          </div>
        </div>

        <!-- Estado vacío: sin archivo -->
        <div v-else class="cm-estado-vacio">
          <i class="bi bi-file-earmark-excel cm-estado-vacio__icono"></i>
          <p class="mb-0">Seleccione un archivo para comenzar</p>
        </div>

      </div>
    </div>

    <!-- --- PASO 2: Vista previa de productos --- -->
    <div v-if="filas.length > 0" class="cm-seccion mb-4">
      <div class="cm-seccion__cabecera d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>
          <i class="bi bi-2-circle-fill me-2"></i>
          Vista previa —
          <strong>{{ filas.length }}</strong> productos
          <span v-if="filasConError > 0" class="badge bg-danger ms-2">{{ filasConError }} con error</span>
          <span v-if="filasValidas > 0" class="cm-badge-exito ms-2">{{ filasValidas }} válidos</span>
        </span>
        <div class="form-check form-switch mb-0">
          <input class="form-check-input" type="checkbox" id="soloErrores" v-model="soloMostrarErrores" />
          <label class="form-check-label small cm-texto-secundario" for="soloErrores">Solo errores</label>
        </div>
      </div>

      <div class="table-responsive" style="max-height:520px;overflow-y:auto">
        <table class="cm-tabla">
          <thead>
            <tr>
              <th style="width:36px">#</th>
              <th>Cód. Principal</th>
              <th>Cód. Secundario</th>
              <th>Producto</th>
              <th class="text-end">Costo</th>
              <th class="text-end">P. Venta 1 <span class="text-danger">*</span></th>
              <th class="text-end">P. Venta 2</th>
              <th class="text-end">P. Venta 3</th>
              <th class="text-end">P. Venta 4</th>
              <th class="text-end">P. Venta 5</th>
              <th class="text-end">Exist.</th>
              <th class="text-end">Mín</th>
              <th class="text-end">Máx</th>
              <th>Categoría</th>
              <th style="width:90px">Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(fila, idx) in filasMostradas"
              :key="idx"
              :class="claseFila(fila)"
            >
              <td class="cm-celda-muted">{{ fila._numero }}</td>
              <td>
                <code class="cm-codigo">{{ fila.codigo }}</code>
                <span v-if="fila._existe" class="cm-badge-aviso ms-1" title="Ya existe en el sistema">
                  <i class="bi bi-arrow-repeat"></i>
                </span>
              </td>
              <td><code class="cm-codigo cm-codigo--aux">{{ fila.codigo_auxiliar || '—' }}</code></td>
              <td class="cm-celda-nombre" :title="fila.producto">{{ fila.producto }}</td>
              <td class="text-end">${{ formatNum(fila.p_costo) }}</td>
              <td class="text-end" :class="fila.p_venta1 > 0 && fila.p_venta1 < fila.p_costo ? 'cm-texto-error' : ''">${{ formatNum(fila.p_venta1) }}</td>
              <td class="text-end cm-celda-muted">{{ fila.p_venta2 > 0 ? '$' + formatNum(fila.p_venta2) : '—' }}</td>
              <td class="text-end cm-celda-muted">{{ fila.p_venta3 > 0 ? '$' + formatNum(fila.p_venta3) : '—' }}</td>
              <td class="text-end cm-celda-muted">{{ fila.p_venta4 > 0 ? '$' + formatNum(fila.p_venta4) : '—' }}</td>
              <td class="text-end cm-celda-muted">{{ fila.p_venta5 > 0 ? '$' + formatNum(fila.p_venta5) : '—' }}</td>
              <td class="text-end">{{ fila.existencia }}</td>
              <td class="text-end cm-celda-muted">{{ fila.inv_minimo }}</td>
              <td class="text-end cm-celda-muted">{{ fila.inv_maximo }}</td>
              <td class="cm-celda-nombre" :title="fila.categoria">{{ fila.categoria || '—' }}</td>
              <td>
                <div v-if="fila._errores.length > 0">
                  <span class="badge bg-danger cm-badge-estado" :title="fila._errores.join(', ')">
                    <i class="bi bi-x-circle me-1"></i>Error
                  </span>
                  <div v-for="e in fila._errores" :key="e" class="small text-danger mt-1 cm-msg-estado">{{ e }}</div>
                </div>
                <div v-else-if="fila._advertencias.length > 0">
                  <span class="cm-badge-aviso cm-badge-estado" :title="fila._advertencias.join(', ')">
                    <i class="bi bi-exclamation-triangle me-1"></i>Aviso
                  </span>
                  <div v-for="a in fila._advertencias" :key="a" class="small mt-1 cm-msg-estado cm-texto-aviso">{{ a }}</div>
                </div>
                <span v-else class="cm-badge-exito cm-badge-estado">
                  <i class="bi bi-check-circle me-1"></i>OK
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- --- PASO 3: Importar --- -->
    <div v-if="filasValidas > 0" class="cm-seccion mb-4">
      <div class="cm-seccion__cabecera">
        <i class="bi bi-3-circle-fill me-2"></i>Importar productos
      </div>
      <div class="cm-seccion__cuerpo">

        <!-- Barra de progreso -->
        <div v-if="importando" class="mb-3">
          <div class="d-flex justify-content-between mb-1">
            <small class="cm-texto-secundario">Importando {{ progresoActual }} de {{ totalImportar }}…</small>
            <small class="cm-texto-secundario">{{ Math.round(porcentajeProgreso) }}%</small>
          </div>
          <div class="progress cm-barra-progreso">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated cm-barra-progreso__fill"
              :style="{ width: porcentajeProgreso + '%' }"
            ></div>
          </div>
        </div>

        <!-- Resumen de importación -->
        <div v-if="resultadoImportacion" class="row g-3 mb-3">
          <div class="col-6 col-md-3">
            <div class="cm-resumen cm-resumen--exito">
              <div class="cm-resumen__num">{{ resultadoImportacion.creados }}</div>
              <div class="cm-resumen__label">Creados</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="cm-resumen cm-resumen--aviso">
              <div class="cm-resumen__num">{{ resultadoImportacion.actualizados }}</div>
              <div class="cm-resumen__label">Actualizados</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="cm-resumen cm-resumen--neutro">
              <div class="cm-resumen__num">{{ resultadoImportacion.ignorados }}</div>
              <div class="cm-resumen__label">Ignorados</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="cm-resumen cm-resumen--error">
              <div class="cm-resumen__num">{{ resultadoImportacion.fallidos }}</div>
              <div class="cm-resumen__label">Fallidos</div>
            </div>
          </div>
        </div>

        <!-- Errores de importación -->
        <div v-if="erroresImportacion.length > 0" class="alert alert-warning mb-3">
          <strong><i class="bi bi-exclamation-triangle me-1"></i>Productos no importados:</strong>
          <ul class="mb-0 mt-2 small">
            <li v-for="(err, i) in erroresImportacion" :key="i">
              <strong>{{ err.codigo }}</strong>: {{ err.mensaje }}
            </li>
          </ul>
        </div>

        <!-- Alertas globales -->
        <div v-if="mensajeError" class="alert alert-danger d-flex align-items-center gap-2 mb-3" role="alert">
          <i class="bi bi-x-circle-fill fs-5 flex-shrink-0"></i>
          <div>{{ mensajeError }}</div>
          <button type="button" class="btn-close ms-auto" @click="mensajeError = ''"></button>
        </div>
        <div v-if="mensajeExito" class="alert alert-success d-flex align-items-center gap-2 mb-3" role="alert">
          <i class="bi bi-check-circle-fill fs-5 flex-shrink-0"></i>
          <div>{{ mensajeExito }}</div>
          <button type="button" class="btn-close ms-auto" @click="mensajeExito = ''"></button>
        </div>

        <div class="d-flex gap-2 flex-wrap">
          <button
            v-if="puedeCrear"
            class="btn btn-primary px-4"
            :disabled="importando || importacionCompletada"
            @click="iniciarImportacion"
          >
            <span v-if="importando" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bi bi-cloud-upload me-2"></i>
            {{ importacionCompletada ? 'Importación completada' : `Importar ${filasValidas} productos` }}
          </button>
          <button v-if="importacionCompletada" class="btn btn-outline-secondary" @click="limpiarTodo">
            <i class="bi bi-arrow-repeat me-1"></i>Nueva importación
          </button>
          <router-link to="/inventario/productos" class="btn btn-outline-primary">
            <i class="bi bi-box-seam me-1"></i>Ver productos
          </router-link>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { inventarioService } from '@/services/inventarioService'
import api from '@/services/api'

const CAMPOS_MAPEO = [
  { key: 'codigo',         etiqueta: 'Código principal',  requerido: true,  aliasDefecto: ['código', 'codigo', 'cod', 'sku', 'code', 'codigo principal', 'código principal'] },
  { key: 'codigo_auxiliar',etiqueta: 'Código secundario', requerido: false, aliasDefecto: ['código secundario', 'codigo secundario', 'codigo auxiliar', 'cod. secundario', 'ean', 'barcode', 'código auxiliar'] },
  { key: 'producto',       etiqueta: 'Nombre producto',   requerido: true,  aliasDefecto: ['producto', 'nombre', 'descripcion', 'descripción', 'description', 'name'] },
  { key: 'p_costo',        etiqueta: 'Precio costo',      requerido: false, aliasDefecto: ['p. costo', 'precio costo', 'costo', 'cost', 'precio base', 'p.costo', 'p_costo'] },
  { key: 'p_venta1',       etiqueta: 'P. Venta 1',        requerido: false, aliasDefecto: ['p. venta', 'precio venta', 'venta', 'price', 'p.venta', 'pventa', 'p_venta', 'precio 1', 'p. venta 1', 'p_venta1', 'p.venta1'] },
  { key: 'p_venta2',       etiqueta: 'P. Venta 2',        requerido: false, aliasDefecto: ['p. mayoreo', 'precio mayoreo', 'mayoreo', 'wholesale', 'p.mayoreo', 'precio 2', 'p_venta2', 'p. venta 2', 'p.venta2'] },
  { key: 'p_venta3',       etiqueta: 'P. Venta 3',        requerido: false, aliasDefecto: ['precio 3', 'p_venta3', 'p. venta 3', 'p.venta3'] },
  { key: 'p_venta4',       etiqueta: 'P. Venta 4',        requerido: false, aliasDefecto: ['precio 4', 'p_venta4', 'p. venta 4', 'p.venta4'] },
  { key: 'p_venta5',       etiqueta: 'P. Venta 5',        requerido: false, aliasDefecto: ['precio 5', 'p_venta5', 'p. venta 5', 'p.venta5'] },
  { key: 'existencia',     etiqueta: 'Existencia',        requerido: false, aliasDefecto: ['existencia', 'stock', 'cantidad', 'qty', 'inventario'] },
  { key: 'inv_minimo',     etiqueta: 'Stock mínimo',      requerido: false, aliasDefecto: ['inv. mínimo', 'inv minimo', 'mínimo', 'minimo', 'min', 'stock minimo', 'inv.minimo'] },
  { key: 'inv_maximo',     etiqueta: 'Stock máximo',      requerido: false, aliasDefecto: ['inv. máximo', 'inv maximo', 'máximo', 'maximo', 'max', 'stock maximo', 'inv.maximo'] },
  { key: 'categoria',      etiqueta: 'Categoría',         requerido: false, aliasDefecto: ['categoria', 'categoría', 'departamento', 'department', 'category'] },
  { key: 'unidad_medida',  etiqueta: 'Unidad de medida',  requerido: false, aliasDefecto: ['unidad', 'unidades', 'unidad medida', 'unidad de medida', 'um', 'u/m', 'unidad_medida', 'medida'] },
]

export default {
  name: 'ClientComprasCargaMasiva',

  data() {
    return {
      // Archivo
      archivoSeleccionado: null,
      datosRaw: [],              // Filas crudas del Excel (antes de mapear)
      columnasArchivo: [],
      mapeoColumnas: {},
      procesandoArchivo: false,
      mostrarAvanzado: false,

      // Configuración
      ivaPorDefecto: 15,
      accionDuplicado: 'actualizar_todo',

      // Productos procesados
      filas: [],
      camposMapeo: CAMPOS_MAPEO,
      soloMostrarErrores: false,

      // Datos auxiliares
      categorias: [],
      unidadesMedida: [],

      // Importación
      importando: false,
      importacionCompletada: false,
      progresoActual: 0,
      totalImportar: 0,
      resultadoImportacion: null,
      erroresImportacion: [],

      // Mensajes
      mensajeError: '',
      mensajeExito: '',
    }
  },

  computed: {
    puedeCrear() {
      return this.$store.getters.puedoHacer('/inventario/stock', 'crear')
    },
    filasConError() {
      return this.filas.filter(f => f._errores.length > 0).length
    },
    filasValidas() {
      return this.filas.filter(f => f._errores.length === 0).length
    },
    filasMostradas() {
      return this.soloMostrarErrores
        ? this.filas.filter(f => f._errores.length > 0)
        : this.filas
    },
    porcentajeProgreso() {
      if (this.totalImportar === 0) return 0
      return (this.progresoActual / this.totalImportar) * 100
    },
    mapeoValido() {
      return CAMPOS_MAPEO.filter(c => c.requerido).every(c => !!this.mapeoColumnas[c.key])
    },
    urlPlantilla() {
      const encabezados = [
        'Código', 'Código Sec.', 'Producto', 'Costo',
        'P. Venta 1', 'P. Venta 2', 'P. Venta 3', 'P. Venta 4', 'P. Venta 5',
        'Existencia', 'Stock Mín', 'Stock Máx', 'Categoría',
      ]
      const ejemplo = [
        'EC120', 'BAR-7890', 'Boquilla Adaptadora 1/2"', '2.02',
        '2.60', '2.50', '', '', '',
        '6', '0', '0', 'Eléctrico',
      ]
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([encabezados, ejemplo])
      XLSX.utils.book_append_sheet(wb, ws, 'Productos')
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'base64' })
      return `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${wbout}`
    },
  },

  created() {
    this.cargarDatosAuxiliares()
    this.inicializarMapeo()
  },

  methods: {
    /* ── Datos auxiliares ── */
    async cargarDatosAuxiliares() {
      try {
        const [catRes, umRes] = await Promise.all([
          inventarioService.getCategorias(),
          inventarioService.getUnidadesMedida(true),
        ])
        this.categorias = catRes?.data || []
        this.unidadesMedida = umRes?.data || []
      } catch (err) {
        console.error('Error cargando datos auxiliares:', err)
      }
    },

    inicializarMapeo() {
      const mapeo = {}
      CAMPOS_MAPEO.forEach(c => { mapeo[c.key] = '' })
      this.mapeoColumnas = mapeo
    },

    /* ── Paso 1a: seleccionar archivo → extraer columnas, NO mostrar preview ── */
    onArchivoSeleccionado(e) {
      const archivo = e.target.files[0]
      this.archivoSeleccionado = archivo || null
      this.filas = []
      this.datosRaw = []
      this.columnasArchivo = []
      this.inicializarMapeo()
      this.resultadoImportacion = null
      this.erroresImportacion = []
      this.importacionCompletada = false
      this.mensajeError = ''
      this.mensajeExito = ''

      if (!archivo) return
      this.leerColumnas(archivo)
    },

    async leerColumnas(archivo) {
      try {
        const buffer = await this.leerArchivoComoBuffer(archivo)
        const wb = XLSX.read(buffer, { type: 'array' })
        const hoja = wb.Sheets[wb.SheetNames[0]]
        const datos = XLSX.utils.sheet_to_json(hoja, { defval: '', raw: false })

        if (!datos || datos.length === 0) {
          this.mensajeError = 'El archivo está vacío o no tiene datos válidos.'
          return
        }

        this.datosRaw = datos
        this.columnasArchivo = Object.keys(datos[0])

        // Auto-mapeo inteligente por alias
        CAMPOS_MAPEO.forEach(campo => {
          const encontrada = this.columnasArchivo.find(col =>
            campo.aliasDefecto.includes(col.toLowerCase().trim())
          )
          this.mapeoColumnas[campo.key] = encontrada || ''
        })
      } catch (err) {
        this.mensajeError = 'Error al leer el archivo. Verifique que sea un Excel válido (.xlsx / .xls).'
      }
    },

    /* ── Paso 1b: aplicar mapeo → construir y validar filas ── */
    async aplicarMapeo() {
      if (!this.mapeoValido || this.datosRaw.length === 0) return
      this.procesandoArchivo = true
      this.mensajeError = ''
      this.filas = []

      try {
        // Obtener códigos existentes para marcar duplicados
        let codigosExistentes = new Set()
        try {
          const res = await inventarioService.getProductos({ limit: 9999 })
          const lista = res?.data || []
          lista.forEach(p => codigosExistentes.add(String(p.codigo_principal || '').trim()))
        } catch (_) { /* no crítico */ }

        const mapeo = this.mapeoColumnas
        const obtener = (row, key) => {
          const col = mapeo[key]
          return col ? String(row[col] ?? '').trim() : ''
        }

        this.filas = this.datosRaw.map((row, i) => {
          const fila = {
            _numero: i + 2,
            codigo:          obtener(row, 'codigo'),
            codigo_auxiliar: obtener(row, 'codigo_auxiliar'),
            producto:        obtener(row, 'producto'),
            p_costo:         this.parsearNumero(obtener(row, 'p_costo')),
            p_venta1:        this.parsearNumero(obtener(row, 'p_venta1')),
            p_venta2:        this.parsearNumero(obtener(row, 'p_venta2')),
            p_venta3:        this.parsearNumero(obtener(row, 'p_venta3')),
            p_venta4:        this.parsearNumero(obtener(row, 'p_venta4')),
            p_venta5:        this.parsearNumero(obtener(row, 'p_venta5')),
            existencia:      this.parsearNumero(obtener(row, 'existencia')),
            inv_minimo:      this.parsearNumero(obtener(row, 'inv_minimo')),
            inv_maximo:      this.parsearNumero(obtener(row, 'inv_maximo')),
            categoria:       obtener(row, 'categoria'),
            unidad_medida:   obtener(row, 'unidad_medida'),
            _errores: [],
            _advertencias: [],
            _existe: false,
          }

          // Validaciones obligatorias
          if (!fila.codigo) fila._errores.push('Código principal vacío')
          if (!fila.producto) fila._errores.push('Nombre de producto vacío')
          if (fila.p_costo < 0) fila._errores.push('Precio costo no puede ser negativo')

          // Advertencias
          if (fila.p_venta1 > 0 && fila.p_venta1 < fila.p_costo) {
            fila._advertencias.push('P. Venta 1 menor al costo')
          }
          if (fila.inv_maximo > 0 && fila.inv_minimo > fila.inv_maximo) {
            fila._advertencias.push('Stock mínimo mayor al máximo')
          }

          // Marcar duplicado
          if (fila.codigo && codigosExistentes.has(fila.codigo)) {
            fila._existe = true
          }

          return fila
        })
      } catch (err) {
        this.mensajeError = 'Error al procesar el archivo: ' + (err.message || 'error desconocido')
      } finally {
        this.procesandoArchivo = false
      }
    },

    /* ── Clase de fila según estado ── */
    claseFila(fila) {
      if (fila._errores.length > 0) return 'cm-fila--error'
      if (fila._advertencias.length > 0) return 'cm-fila--aviso'
      if (fila._existe) return 'cm-fila--existente'
      return ''
    },

    /* ── Importación ── */
    async iniciarImportacion() {
      const filasAImportar = this.filas.filter(f => f._errores.length === 0)
      if (filasAImportar.length === 0) return

      this.importando = true
      this.progresoActual = 0
      this.totalImportar = filasAImportar.length
      this.resultadoImportacion = { creados: 0, actualizados: 0, ignorados: 0, fallidos: 0 }
      this.erroresImportacion = []
      this.mensajeError = ''
      this.mensajeExito = ''

      const mapaCategoria = await this.resolverCategorias(filasAImportar)

      const LOTE = 10
      for (let i = 0; i < filasAImportar.length; i += LOTE) {
        const lote = filasAImportar.slice(i, i + LOTE)
        await Promise.all(lote.map(fila => this.importarFila(fila, mapaCategoria)))
        this.progresoActual = Math.min(i + LOTE, filasAImportar.length)
      }

      this.importando = false
      this.importacionCompletada = true
      const r = this.resultadoImportacion
      this.mensajeExito = `Importación completada: ${r.creados} creados, ${r.actualizados} actualizados, ${r.ignorados} ignorados, ${r.fallidos} fallidos.`
    },

    async resolverCategorias(filas) {
      const mapa = {}
      const nombres = [...new Set(filas.map(f => f.categoria).filter(Boolean))]

      for (const nombre of nombres) {
        const existente = this.categorias.find(
          c => c.categoria?.toLowerCase().trim() === nombre.toLowerCase().trim()
        )
        if (existente) {
          mapa[nombre] = existente.id
        } else {
          try {
            const res = await inventarioService.createCategoria({ categoria: nombre })
            const nuevaId = res?.id || res?.data?.id
            mapa[nombre] = nuevaId
            this.categorias.push({ id: nuevaId, categoria: nombre })
          } catch (err) {
            console.warn(`No se pudo crear la categoría "${nombre}":`, err)
          }
        }
      }
      return mapa
    },

    async importarFila(fila, mapaCategoria) {
      try {
        const categoriaId = fila.categoria
          ? (mapaCategoria[fila.categoria] || this.categorias[0]?.id || null)
          : (this.categorias[0]?.id || null)
        // Resolver unidad de medida por nombre/abreviatura del Excel, o tomar la primera como fallback
        let unidadId = this.unidadesMedida.length > 0 ? this.unidadesMedida[0].id : null
        if (fila.unidad_medida) {
          const txt = fila.unidad_medida.toLowerCase().trim()
          const encontrada = this.unidadesMedida.find(
            u => u.unidad_medida?.toLowerCase().trim() === txt ||
                 u.abreviatura?.toLowerCase().trim()    === txt ||
                 u.codigo?.toLowerCase().trim()         === txt ||
                 txt.startsWith(u.unidad_medida?.toLowerCase().trim()) ||
                 u.unidad_medida?.toLowerCase().trim().startsWith(txt)
          )
          if (encontrada) unidadId = encontrada.id
        }

        // Verificar si existe en inventario
        let productoExistente = null
        try {
          const busq = await inventarioService.getProductos({ filtros: { codigo_principal: fila.codigo } })
          const lista = busq?.data || []
          productoExistente = lista.find(p => p.codigo_principal === fila.codigo) || null
        } catch (_) { /* ignorar */ }

        if (productoExistente) {
          // ── Producto existente ──
          if (this.accionDuplicado === 'solo_crear' || this.accionDuplicado === 'ignorar') {
            this.resultadoImportacion.ignorados++
            return
          }

          if (this.accionDuplicado === 'actualizar_precios' || this.accionDuplicado === 'actualizar_todo') {
            await inventarioService.updateProducto(productoExistente.id, {
              precio_unitario: fila.p_costo,
              costo:           fila.p_costo,
              codigo_auxiliar: fila.codigo_auxiliar || productoExistente.codigo_auxiliar,
              ...(this.accionDuplicado === 'actualizar_todo' && {
                categoria_id: categoriaId || productoExistente.categoria_id,
                stock_minimo:  fila.inv_minimo || productoExistente.stock_minimo,
                stock_maximo:  fila.inv_maximo || productoExistente.stock_maximo,
              }),
            })
            const precios = this.construirPrecios(fila)
            if (precios.length > 0) await inventarioService.savePrecios(productoExistente.id, precios)
          }

          if (
            (this.accionDuplicado === 'actualizar_stock' || this.accionDuplicado === 'actualizar_todo') &&
            fila.existencia > 0
          ) {
            await this.ajustarStock(productoExistente.id, fila.existencia)
          }

          this.resultadoImportacion.actualizados++

        } else {
          // ── Producto nuevo ──
          const payload = {
            codigo_principal:  fila.codigo,
            codigo_auxiliar:   fila.codigo_auxiliar || null,
            nombre_producto:   fila.producto,
            tipo_producto:     'bien',
            precio_unitario:   fila.p_costo,
            costo:             fila.p_costo,
            impuesto_id:       1,
            tarifa_impuesto:   this.ivaPorDefecto,
            categoria_id:      categoriaId,
            unidad_medida_id:  unidadId,
            descuento:         0,
            stock_minimo:      fila.inv_minimo || 0,
            stock_maximo:      fila.inv_maximo || 0,
          }
          const res = await inventarioService.createProducto(payload)
          const productoId = res?.id
          if (!productoId) throw new Error('No se recibió ID del producto creado')

          const precios = this.construirPrecios(fila)
          if (precios.length > 0) await inventarioService.savePrecios(productoId, precios)
          if (fila.existencia > 0) await this.ajustarStock(productoId, fila.existencia)

          this.resultadoImportacion.creados++
        }
      } catch (err) {
        this.resultadoImportacion.fallidos++
        this.erroresImportacion.push({
          codigo: fila.codigo,
          mensaje: err?.response?.data?.message || err.message || 'Error desconocido',
        })
      }
    },

    construirPrecios(fila) {
      const precios = []
      if (fila.p_venta1 > 0) precios.push({ precio: fila.p_venta1, activo: 1 })
      if (fila.p_venta2 > 0) precios.push({ precio: fila.p_venta2, activo: 1 })
      if (fila.p_venta3 > 0) precios.push({ precio: fila.p_venta3, activo: 1 })
      if (fila.p_venta4 > 0) precios.push({ precio: fila.p_venta4, activo: 1 })
      if (fila.p_venta5 > 0) precios.push({ precio: fila.p_venta5, activo: 1 })
      return precios
    },

    async ajustarStock(productoId, cantidad) {
      try {
        await api.post('/stock/ajuste', {
          producto_id:  productoId,
          cantidad,
          observacion:  'Carga masiva inicial',
        })
      } catch (err) {
        console.warn(`No se pudo ajustar stock para producto ${productoId}:`, err.message)
      }
    },

    /* ── Utilidades ── */
    leerArchivoComoBuffer(archivo) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(new Uint8Array(e.target.result))
        reader.onerror = reject
        reader.readAsArrayBuffer(archivo)
      })
    },

    parsearNumero(valor) {
      if (valor === '' || valor === null || valor === undefined) return 0
      // Eliminar símbolos de moneda y espacios conservando separadores
      let limpio = String(valor).replace(/[$\s]/g, '').trim()
      if (limpio === '') return 0
      const tieneComa  = limpio.includes(',')
      const tienePunto = limpio.includes('.')
      if (tieneComa && tienePunto) {
        // Detectar formato: anglosajón "1,234.56" vs europeo "1.234,56"
        if (limpio.lastIndexOf(',') > limpio.lastIndexOf('.')) {
          // Europeo: eliminar punto de miles, coma → punto decimal
          limpio = limpio.replace(/\./g, '').replace(',', '.')
        } else {
          // Anglosajón: eliminar coma de miles
          limpio = limpio.replace(/,/g, '')
        }
      } else if (tieneComa) {
        // Solo coma: "1,50" (decimal) vs "1,500" (miles)
        const partes = limpio.split(',')
        if (partes.length === 2 && partes[1].length <= 2) {
          limpio = limpio.replace(',', '.') // coma decimal → punto
        } else {
          limpio = limpio.replace(/,/g, '') // coma de miles → eliminar
        }
      }
      const num = parseFloat(limpio)
      return isNaN(num) ? 0 : num
    },

    formatNum(val) {
      return parseFloat(val || 0).toFixed(2)
    },

    limpiarTodo() {
      this.archivoSeleccionado = null
      this.datosRaw = []
      this.filas = []
      this.columnasArchivo = []
      this.inicializarMapeo()
      this.resultadoImportacion = null
      this.erroresImportacion = []
      this.importacionCompletada = false
      this.mensajeError = ''
      this.mensajeExito = ''
      if (this.$refs.inputArchivo) this.$refs.inputArchivo.value = ''
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ── */
.cm-contenedor {
  padding: 1.5rem;
  color: var(--text-primary);
}

/* ── Encabezado ── */
.cm-titulo {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary);
}
.cm-subtitulo {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

/* ── Secciones (igual patrón que Registrar Compra) ── */
.cm-seccion {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.cm-seccion__cabecera {
  background: var(--primary-color, var(--primary-main, #0ea5e9));
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.6rem 1.1rem;
  letter-spacing: 0.01em;
}

.cm-seccion__cuerpo {
  padding: 1.25rem;
}

/* ── Panel avanzado ── */
.cm-panel-avanzado {
  background: var(--secondary-bg-soft, #f8fafc);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  padding: 1rem;
}

.dark-theme .cm-panel-avanzado {
  background: var(--bg-tertiary, #1c2535);
}

.cm-link-avanzado {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.85rem;
}
.cm-link-avanzado:hover {
  color: var(--primary-color, var(--primary-main));
}

/* ── Estado vacío ── */
.cm-estado-vacio {
  text-align: center;
  padding: 2.5rem 1rem;
  color: var(--text-muted);
}
.cm-estado-vacio__icono {
  font-size: 3rem;
  display: block;
  margin-bottom: 0.75rem;
  color: var(--border-main);
}

/* ── Tabla de previsualización ── */
.cm-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.825rem;
  min-width: 1200px;
}

.cm-tabla thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.cm-tabla thead tr {
  background: var(--secondary-bg-soft, #f1f5f9);
}

.dark-theme .cm-tabla thead tr {
  background: var(--bg-tertiary, #1c2535);
}

.cm-tabla th {
  padding: 0.55rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 2px solid var(--border-main);
  white-space: nowrap;
}

.cm-tabla td {
  padding: 0.4rem 0.65rem;
  border-bottom: 1px solid var(--border-main);
  vertical-align: middle;
}

.cm-tabla tbody tr:hover td {
  background: var(--table-hover, rgba(14, 165, 233, 0.05));
}

/* Filas con estado */
.cm-fila--error td { background: rgba(239, 68, 68, 0.06); }
.cm-fila--aviso  td { background: rgba(245, 158, 11, 0.06); }
.cm-fila--existente td { background: rgba(245, 158, 11, 0.03); }

/* ── Helpers de texto ── */
.cm-texto-primario   { color: var(--text-primary); }
.cm-texto-secundario { color: var(--text-secondary); }
.cm-texto-muted      { color: var(--text-muted); }
.cm-texto-error      { color: var(--accent-danger, #ef4444); }
.cm-texto-aviso      { color: var(--accent-warning, #f59e0b); }

/* ── Celdas especiales ── */
.cm-celda-muted { color: var(--text-secondary); font-size: 0.8rem; }
.cm-celda-nombre {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Códigos ── */
.cm-codigo {
  font-size: 0.78rem;
  color: var(--text-primary);
}
.cm-codigo--aux {
  color: var(--text-secondary);
}

/* ── Badges ── */
.cm-badge-primary {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--primary-color, var(--primary-main, #0ea5e9));
  color: #fff;
}

.cm-badge-exito {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--accent-success, #10b981);
  color: #fff;
}

.cm-badge-aviso {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--accent-warning, #f59e0b);
  color: #1a1a1a;
}

.cm-badge-estado {
  font-size: 0.7rem !important;
}
.cm-msg-estado {
  font-size: 0.75rem;
}

/* ── Resumen de importación ── */
.cm-resumen {
  border: 1px solid var(--border-main);
  border-radius: 10px;
  text-align: center;
  padding: 0.9rem 0.5rem;
  background: var(--secondary-bg-main);
}
.cm-resumen__num  { font-size: 1.75rem; font-weight: 700; line-height: 1; }
.cm-resumen__label { font-size: 0.8rem; color: var(--text-secondary); margin-top: 0.3rem; }

.cm-resumen--exito .cm-resumen__num  { color: var(--accent-success, #10b981); }
.cm-resumen--aviso .cm-resumen__num  { color: var(--accent-warning, #f59e0b); }
.cm-resumen--neutro .cm-resumen__num { color: var(--text-secondary); }
.cm-resumen--error .cm-resumen__num  { color: var(--accent-danger, #ef4444); }

/* ── Barra de progreso ── */
.cm-barra-progreso {
  height: 10px;
  background: var(--border-main);
  border-radius: 99px;
  overflow: hidden;
}
.cm-barra-progreso__fill {
  background: var(--primary-color, var(--primary-main, #0ea5e9));
  transition: width 0.3s ease;
}
</style>
